import React, { useMemo } from "react";
import { Box, IconButton, Collapse } from "@mui/material";
import clsx from "clsx";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { TitleWithSuperset } from "./TitleWithSuperset";
import { CollapsibleSetSection } from "./CollapsibleSetSection";
import WorkoutHistorySet from "../client-exercise-history/workout-history/WorkoutHistorySet";
import { WorkoutExercise, Sets } from "../workout/types";
import { ExerciseTypeExtra, Units, ExerciseTypeReps } from "../../constants";
import {
  checkIsPartOfSuperset,
  buildNormalizedSet,
  parseNumber,
  parseRange,
  isEmptyPlanValue,
} from "./workoutUtils";
import { workoutHistorySetStyles } from "../client-workouts/ClientWorkoutsChoose";

export type ExerciseHistoryItemClasses = Partial<{
  container: string;
  setRoot: string;
  setDisabled: string;
  supersetIndent: string;
  unitText: string;
  circle: string;
}>;

interface ExerciseHistoryItemProps {
  exercise: WorkoutExercise;
  exIndex: number;
  allExercises: WorkoutExercise[];
  handleExerciseSelect?: (exercise: WorkoutExercise) => void;
  classes?: ExerciseHistoryItemClasses;
  isBadgeOpen?: boolean;
  showOnlyCompleted?: boolean;
  hideCollapsible?: boolean;
  hideHeader?: boolean;
  userUnits?: Units;
  collapseSectionNoLeftMargin?: boolean;
}

type SortedSet = {
  set: Sets;
  originalIndex: number;
};

export const ExerciseHistoryItem: React.FC<ExerciseHistoryItemProps> = ({
  exercise,
  exIndex,
  allExercises,
  handleExerciseSelect,
  classes = {},
  showOnlyCompleted = false,
  hideCollapsible = false,
  hideHeader = false,
  collapseSectionNoLeftMargin = false,
}) => {
  const sWorkoutSet = workoutHistorySetStyles();

  const isPartOfSuperset = useMemo(
    () => checkIsPartOfSuperset(allExercises, exIndex),
    [allExercises, exIndex],
  );

  const originalData = exercise.originalContent;
  const completedSets: Sets[] = exercise.sets || [];
  const hasMissed = completedSets.some((set: Sets) => !set.checked);

  const hasChanges = useMemo(() => {
    if (!originalData?.sets?.length) return false;

    if (originalData.sets.length !== completedSets.length) return true;
    const isRangeReps = exercise.typeReps === ExerciseTypeReps.RANGE;

    return completedSets.some((set, index) => {
      const originalSet = originalData.sets[index];
      if (!originalSet) return true;
      if (!set.checked) return true;

      if (isRangeReps) {
        const actualReps = parseNumber(set.reps);
        const plannedRange = parseRange(originalSet.reps);
        if (actualReps < plannedRange.min || actualReps > plannedRange.max) {
          return true;
        }
      } else if (set.reps !== originalSet.reps) {
        return true;
      }

      const normWeight = (w: unknown) =>
        isEmptyPlanValue(w) ? "" : String(w ?? "");
      if (normWeight(set.weight) !== normWeight(originalSet.weight))
        return true;
      if (set.extraMeasurement !== originalSet.extraMeasurement) return true;

      return false;
    });
  }, [completedSets, originalData, exercise.typeReps]);
  const shouldShowCollapse = hasChanges || hasMissed;
  const allSetsCompleted = !hasMissed;
  const canShowDiff = !!originalData && Array.isArray(originalData.sets);

  const baseSets: SortedSet[] = useMemo(() => {
    return completedSets.map((set, index) => ({
      set,
      originalIndex: index,
    }));
  }, [completedSets]);

  const sortedSets = useMemo(() => {
    return [...baseSets].sort((a, b) => {
      if (a.set.checked === b.set.checked) return 0;
      return a.set.checked ? -1 : 1;
    });
  }, [baseSets]);

  const originalOrderSets = baseSets;

  const renderExerciseContent = (isBadgeOpen: boolean) => {
    const setsToRender = isBadgeOpen ? originalOrderSets : sortedSets;

    return (
      <>
        {!hideHeader && (
          <Box
            sx={{
              overflow: "visible",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TitleWithSuperset
              card={exercise}
              index={exIndex}
              exercises={allExercises}
            />
            <IconButton
              onClick={() => handleExerciseSelect?.(exercise)}
              sx={{ color: "primary.main", p: 0.5 }}
            >
              <InsertChartOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        )}

        {setsToRender.map(({ set: completedSet, originalIndex }, idx) => {
          const isCompleted = !!completedSet.checked;
          if (showOnlyCompleted && !isCompleted) return null;

          const rawPlannedSet = canShowDiff
            ? originalData?.sets?.[originalIndex]
            : null;

          const plannedSet = rawPlannedSet
            ? {
                ...rawPlannedSet,
                typeExtraMeasurement: originalData?.typeExtraMeasurement,
              }
            : null;
          const normalizedSet = buildNormalizedSet({
            completedSet,
            plannedSet,
            userUnits: exercise.units as string,
            originalUnits: originalData?.units || exercise.units,
            typeExtraMeasurement: exercise.typeExtraMeasurement,
            exerciseTypeSet: exercise.typeSet,
          });

          return (
            <Collapse
              key={`set-${originalIndex}`}
              in={isCompleted || isBadgeOpen}
              timeout={300}
              unmountOnExit={false}
            >
              <WorkoutHistorySet
                setNumber={idx}
                set={normalizedSet}
                typeExtraMeasurement={
                  exercise.typeExtraMeasurement ?? ExerciseTypeExtra.NONE
                }
                exerciseTypeSet={exercise.typeSet}
                units={exercise.units as Units}
                classes={{
                  root: clsx(
                    sWorkoutSet.body,
                    classes.setRoot,
                    isPartOfSuperset &&
                      clsx(
                        sWorkoutSet.bodySupersetIndent,
                        classes.supersetIndent,
                      ),
                    !isCompleted && sWorkoutSet.setDisabled,
                  ),
                  unitText: sWorkoutSet.unitText,
                }}
                circleClassName={clsx(sWorkoutSet.smallCircle, classes.circle)}
                showDiff={isBadgeOpen}
              />
            </Collapse>
          );
        })}
      </>
    );
  };

  if (hideCollapsible) {
    return (
      <Box
        data-exercise-index={exIndex}
        sx={{
          mb: hideHeader ? 0 : 2,
          position: "relative",
          overflow: "visible",
        }}
      >
        {renderExerciseContent(true)}
      </Box>
    );
  }

  return (
    <Box
      data-exercise-index={exIndex}
      sx={{ mb: 2, position: "relative", overflow: "visible" }}
    >
      {shouldShowCollapse ? (
        <CollapsibleSetSection
          title="Compare with original workout"
          showCollapse={shouldShowCollapse}
          allSetsCompleted={allSetsCompleted}
          disableLeftMargin={collapseSectionNoLeftMargin}
        >
          {(isBadgeOpen) => renderExerciseContent(isBadgeOpen)}
        </CollapsibleSetSection>
      ) : (
        renderExerciseContent(true)
      )}
    </Box>
  );
};
